import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PscPhrase } from '../../components/psc/psc-phrase';
import DependingObjects from '../../components/psc/lib/DependingObjects'
import CancelButton from '../../components/psc/lib/CancelButton'
import SaveButton from '../../components/psc/lib/SaveButton'
import DeleteButton from '../../components/psc/lib/DeleteButton'
import ErrorMessage from '../../components/psc/lib/ErrorMessage'
import SingleSelect from '../../components/psc/lib/SingleSelect'
import Checkbox from '../../components/psc/lib/Checkbox'
import HasTemplate from '../../components/psc/lib/HasTemplate'

import ReportSchemeBulkSelectModal from './ReportSchemeBulkSelectModal'

import useCadmObject from '../../hooks/useCadmObject'

const ReportScheme = () => {
    let { objID } = useParams()
    const isNew = objID === "new" ? true : false
    objID = parseInt(objID,10)
    const objectUrl = '/v0/report_schemes' + (isNew ? '' : '/' + objID)

    const {
        cadmObjects,
    } = useCadmObject()

    // Fields
    const [name, setName] = useState('')
    const [members, setMembers] = useState([])
    const [reportTypeID, setReportTypeID] = useState(0)
    const [reportTypeSections, setReportTypeSections] = useState([])
    const [hasStages, setHasStages] = useState(false)

    const [previousReportTypeID, setPreviousReportTypeID] = useState(0)
    
    // For the bulk add
    const [showBulkAddModal, setShowBulkAddModal] = useState(false)
    const [usageObjects, setUsageObjects] = useState([])
    const [selectedSection, setSelectedSection] = useState({})
    
    const [template, setTemplate] = useState({})

    // Read-only
    const [isUsed, setIsUsed] = useState(false)
    const [dependingObjects, setDependingObjects] = useState([])
    const [stdDocuments, setStdDocuments] = useState([])
    const [reportTypes, setReportTypes] = useState([])

    // Submit
    const [submitActive, setSubmitActive] = useState(false)
    const [error, setError] = useState(null)
    const [cloneActive, setCloneActive] = useState(false)

    useEffect(() => {
        if ( isNew) {
            return
        }
        fetchObject()
    }, [isNew])

    useEffect(() => {
        fetch('/v0/std_documents', { credentials: 'same-origin' })
        .then(function(response) {
            return response.json()
        })  
        .then(function(json) {
            setStdDocuments(json.results)
        })
    }, [])

    useEffect(() => {
        fetch('/v0/report_types', { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setReportTypes(json.results)
            })
    }, [])

    useEffect(() => {
        let sections = []
        reportTypes.map(function(obj) {
            if (obj.id === reportTypeID) {
                sections = obj.report_type_sections
            }
        })

        if( ! sections ) {
            setReportTypeSections([])
            return
        }
        setReportTypeSections(sections)

        // below can't happen to non new ones
        if( reportTypeID === previousReportTypeID ) {
            return
        }

        let previousSections = []
        reportTypes.map(function(obj) {
            if (obj.id === previousReportTypeID) {
                previousSections = obj.report_type_sections
            }
        })
        
        // User changed Report Type..
        // go through all scheme members, change report section to the same section in the new type, if it exists
        // .log("OLD", this.state.report_type_sections)
        // .log("NEW", members)
        let oldMap = {}
        previousSections.map(function(old) {
            oldMap[old.title] = old.id
        })
        let newMap = {}
        sections.map(function(n) {
            newMap[n.title] = n.id
        })
        let oldToNewMap = {}
        Object.keys(oldMap).map(function(key) {
            if ( newMap[key] ) {
                oldToNewMap[oldMap[key]] = newMap[key]
            }
        })

        let updatedMembers = []
        members.map(function (member) {
            if( oldToNewMap[member.report_type_section_id] ) {
                member.report_type_section_id = oldToNewMap[member.report_type_section_id]
                updatedMembers.push(member)
            }
        })
        setMembers(updatedMembers)
        setPreviousReportTypeID(reportTypeID)

    }, [reportTypeID, reportTypes])

    useEffect(() => {
        const objects = cadmObjects.map(function(orig) {
            const o = JSON.parse(JSON.stringify(orig))
            o.options = stdDocuments.filter(function (d) {
                return d.cadm_object_id === o.id
            })
            return o
        })
        setUsageObjects(objects)
    }, [cadmObjects, stdDocuments])

    const fetchObject = () => {
        fetch(objectUrl, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }

                fromBackend(json.result)
            })
            .catch(function(error) {
                console.log(error)
            })
    }

    const fromBackend = (obj) => {
        if (obj.members) {
            obj.members.map(function (m) {
                m.timestamp = m.id; // just to use same key 'timestamp' for all entries
            })
        } else {
            obj.members = [];
        }

        if (obj.report_type_id) {
            setReportTypeID(obj.report_type_id);
            setPreviousReportTypeID(obj.report_type_id);
        }
        setName(obj.name)
        setHasStages(obj.has_stages)
        setMembers(obj.members)
        setIsUsed(obj.is_used)
        setDependingObjects(obj.depending_objects)
        setTemplate(obj.template)
    }

    const toBackend = () => {
        return {
            name: name.trim(),
            report_type_id: reportTypeID,
            has_stages: hasStages,
            members: members
        }
    }

    const validateBeforeSubmit = () => {
        let errors = []

        if ( ! name ) {
            errors.push('Name is required')
        }

        if ( ! reportTypeID ) {
            errors.push('Report Type is required');
        }

        let memberList = members.map(function (m) {
            m.error = false
            return m
        })
        
        // Empty?? No go
        const empty = memberList.filter(m => m.std_document_id === '')
        if (empty.length > 0) {
            errors.push('All Types are required');
            memberList = memberList.map(function (m) {
                if (m.std_document_id === '' ) {
                    m.error = true
                }
                return m
            })
        }
        
        // check that list of standard document types + report type section id is unique
        let stdDocumentIDs = memberList.filter(m => m.std_document_id !== '').map(function (m) {
            return m.report_type_section_id + '-' + m.std_document_id;
        })
        var uniq = stdDocumentIDs
            .map((name) => {
                return {count: 1, name: name}
            })
            .reduce((a, b) => {
                a[b.name] = (a[b.name] || 0) + b.count
                return a
            }, {})
        var duplicates = Object.keys(uniq).filter((a) => uniq[a] > 1)
        if (duplicates.length > 0) {
            errors.push('A document type has been specified more than once in one section')
            duplicates.map(function(dup) {
                const parts = dup.split('-')
                const sectionId = Number(parts[0]);
                const stdId = Number(parts[1]);
                memberList = memberList.map(function (m) {
                    if( m.report_type_section_id === sectionId && m.std_document_id === stdId ) {
                        m.error = true
                    }
                    return m
                })
            })
        }

        if ( errors.length > 0 ) {
            setMembers(memberList)
            setError(errors)
            return false
        }

        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }
    
    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        return {
            objType: 'report_schemes',
            objID: objID,
            data: toBackend(),
        }
    }

    const clone = (event) => {
        if (event) {
            event.preventDefault()
        }

        if( cloneActive ) {
            return
        }
        setSubmitActive(true)
        setCloneActive(true)

        let url = objectUrl+'/clone'
        return {
            objType: 'report_schemes',
            objID: objID,
            url: url,
            data: toBackend(),
        }
    }

    const handleHasStagesChange = (button) => {
        let has_stages = hasStages
        if( button === 'has_stages' && has_stages ) {
            return
        }
        if( button === 'no_stages' && !has_stages ) {
            return
        }
        
        setHasStages(!has_stages)
    }

    const removeMember = (member) => {
        let updatedMembers = members.filter(function (m) {
            if(m.timestamp === member.timestamp) { return false }
            return true;
        });
        setMembers(updatedMembers)
    }

    const addMember = (sectionID) => {
        if ( ! reportTypeID ) {
            setError('Please specify a Report Type first')
            return
        }
        setError('')

        // timestamp to have a unique key in the table
        let newMembers = [{ id: 0, std_document_id: '', report_type_section_id: sectionID, timestamp: new Date().getTime() }]
        if (members) {
            setMembers(members.concat(newMembers))
        } else {
            setMembers(newMembers)
        }
    }

    const updateMember = (field, member, event) => {
        var value = event.value;

        if( field.match(/_id$/) && value !== '' && parseInt(Number(value)) === value ) {
            value = parseInt(value, 10);
        }

        let updatedMembers = members.filter(function (m) {
            if(m.timestamp === member.timestamp) {
                m[field] = value
            }
            return true;
        });
        setMembers(updatedMembers)
    }

    const bulkAddMembers = (membersToAdd, section) => {
        const toAdd = membersToAdd.map(item => {
            return {
                id: 0,
                cadm_object_id: item.cadm_object_id,
                std_document_id: item.id,
                report_type_section_id: section.id,
                timestamp: new Date().getTime() + Math.floor(Math.random() * 100000),
                is_used: false,
            }
        })
        setMembers([...members, ...toAdd])
    }

    let requiredIndicator = (<label className="form-required-star">&nbsp;*</label>);

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Report Scheme: {name || '<New Report Scheme>'}</h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Name
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required 
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Report Type
                                        {!isUsed && requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                            value={reportTypeID}
                                            onChange={event => setReportTypeID(event.value)}
                                            options={reportTypes}
                                            disabled={isUsed}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Output</label>
                                    <div className="col-sm-8">

                                        <div className="form-check" key="no_stages" style={{paddingTop: '10px'}}>
                                            <Checkbox
                                                id="no_stages"
                                                name="no_stages"
                                                onChange={event => handleHasStagesChange('no_stages')}
                                                isChecked={!hasStages}
                                                disabled={isUsed}
                                            />
                                            <label htmlFor="no_stages">
                                                Status Report
                                            </label>
                                            <span className="help-block m-b-none" style={{marginLeft: '8px'}}>
                                                Snapshot the current state of all project Documents and Records. Use report filters to include items of any Status.
                                            </span>
                                        </div>

                                        <div className="form-check" key="has_stages">
                                            <Checkbox
                                                id="has_stages"
                                                name="has_stages"
                                                onChange={event => handleHasStagesChange('has_stages')}
                                                isChecked={hasStages}
                                                disabled={isUsed}
                                            />
                                            <label htmlFor="has_stages">
                                                Tracking Report
                                            </label>
                                            <span className="help-block m-b-none" style={{marginLeft: '8px'}}>
                                                Track changes to the project's configuration baseline between key milestones and reviews.
                                                Baselines include only current (Live) and previous (Archived) project configuration.
                                            </span>
                                        </div>

                                    </div>
                                </div>

                                <HasTemplate
                                    templateID={template.id}
                                    filename={template.filename}
                                    disabled={false}
                                    visible={true}
                                />
                                
                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Report Section Map</label>
                                    <div className="col-sm-8">

                                        {reportTypeSections.map(function(section) {
                                            return (
                                                <div key={section.id}>
                                                    <h3>{section.title}</h3>

                                                    <table className="table table-striped table-bordered table-hover dataTable" >
                                                        <thead>
                                                            <tr>
                                                                <th>Configured Item</th>
                                                                <th>Type</th>
                                                                <th>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tfoot>
                                                            <tr>
                                                                <th>Configured Item</th>
                                                                <th>Type</th>
                                                                <th>Action</th>
                                                            </tr>
                                                        </tfoot>
                                                        <tbody>
                                                            {members.map(function(obj) {
                                                                if( obj.report_type_section_id !== section.id ) {
                                                                    return
                                                                }
                                                                return (
                                                                    <tr key={obj.id || obj.timestamp} className={(obj.error) ? 'error' : ''}>
                                                                        <td>
                                                                            <input type="hidden" name="report_type_section_id" value={obj.report_type_section_id} />

                                                                            <SingleSelect
                                                                                name="cadm_object_id"
                                                                                value={obj.cadm_object_id}
                                                                                onChange={event => updateMember('cadm_object_id', obj, event)}
                                                                                options={cadmObjects}
                                                                                disabled={obj.is_used}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <SingleSelect
                                                                                name="std_document_id"
                                                                                value={obj.std_document_id}
                                                                                onChange={event => updateMember('std_document_id', obj, event)}
                                                                                options={stdDocuments.filter(item => item.cadm_object_id === obj.cadm_object_id)}
                                                                                disabled={obj.is_used}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            {obj.is_used === true &&
                                                                             <DependingObjects list={obj.depending_objects} size="xs" />
                                                                            }
                                                                            {obj.is_used !== true &&
                                                                             <button type="button" className="btn btn-xs btn-warning" onClick={event => removeMember(obj)}>Remove</button>
                                                                            }
                                                                        </td>
                                                                    </tr>
                                                                )
                                                             })}
                                                    <tr>
                                                        <td>&nbsp;</td>
                                                        <td>&nbsp;</td>
                                                        <td>
                                                            <button type="button" className="btn btn-xs btn-primary" onClick={event => addMember(section.id)}>Add</button>
                                                                &nbsp;
                                                                    <button 
                                                                        type="button" 
                                                                        className="btn btn-xs btn-light" 
                                                                        onClick={() => { setSelectedSection(section); setShowBulkAddModal(true) }}>
                                                                            Bulk add
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                    <div>
                                                            &nbsp;
                                                    </div>
                                                </div>
                                            )
                                        })}

                                    </div>
                                </div>
                                
                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />
                                        
                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={true}
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        <SaveButton
                                            buttonVariant='warning'
                                            visible={!isNew}
                                            spinnerActive={cloneActive}
                                            label='Clone'
                                            onClick={clone}
                                            onError={handleSubmitError}
                                        />
                                        
                                        <DependingObjects list={dependingObjects} />
        
                                        <DeleteButton
                                           isHidden={isNew || isUsed}
                                           url={objectUrl}
                                           onError={handleSubmitError}
                                        />

                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <ReportSchemeBulkSelectModal 
                items={usageObjects}
                section={selectedSection}
                bulkAdd={bulkAddMembers} 
                show={showBulkAddModal} 
                onHide={() => setShowBulkAddModal(false)}
            />
        </div>
    )
};

export default ReportScheme
